// Badges showing how a result compares with the athlete's previous result and
// with their personal best for the same workout. Pairs with PrognosticBadges.

function formatDelta(seconds) {
  const rounded = Math.round(Math.abs(seconds) * 10) / 10;
  if (rounded === 0) return null;
  return `${seconds < 0 ? '\u2212' : '+'}${rounded.toFixed(1)}s`;
}

// Stored dates are MM/DD/YYYY. Drop the year when it's the current one to keep
// the badge short in the results table.
function formatShortDate(value) {
  if (!value) return null;
  const [month, day, year] = String(value).split('/').map((part) => parseInt(part, 10));
  if (!month || !day || !year) return value;
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(year === new Date().getFullYear() ? {} : { year: 'numeric' }),
  });
}

function deltaParts(comparison) {
  if (!comparison?.previous) return null;
  const useSplit = comparison.compareOn === 'split';
  const raw = useSplit ? comparison.deltaSplitSeconds : comparison.deltaSeconds;
  if (raw == null || !Number.isFinite(raw)) return null;

  const text = formatDelta(raw);
  const suffix = useSplit ? '/500m' : '';
  return {
    label: text ? `${text}${suffix}` : 'Even',
    faster: raw < 0,
    even: text == null,
    title: `Compared with ${comparison.previousDate} (${
      useSplit ? 'split' : 'total time'
    }) \u2014 same boat class, result type and piece structure`,
  };
}

function personalBestParts(comparison) {
  if (!comparison || comparison.isBest || !comparison.personalBestDate) return null;
  const useSplit = comparison.compareOn === 'split';
  const raw = useSplit ? comparison.deltaToBestSplitSeconds : comparison.deltaToBestSeconds;
  if (raw == null || !Number.isFinite(raw)) return null;

  const text = formatDelta(raw);
  const suffix = useSplit ? '/500m' : '';
  return {
    label: text ? `${text}${suffix} vs PB` : 'Matched PB',
    matched: text == null,
    date: formatShortDate(comparison.personalBestDate),
    title: `Personal best for this workout was set on ${comparison.personalBestDate}`,
  };
}

export function ResultComparisonBadges({ comparison, className = '', compact = false }) {
  const delta = deltaParts(comparison);
  const pb = personalBestParts(comparison);
  if (!comparison || (!delta && !pb && !comparison.isBest)) return null;

  const size = compact ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-0.5';
  const tone = delta?.even
    ? 'bg-gray-100 text-gray-600'
    : delta?.faster
      ? 'bg-green-100 text-green-700'
      : 'bg-red-100 text-red-700';

  return (
    <div className={`flex flex-wrap items-center gap-1 ${className}`}>
      {comparison.isBest && (
        <span
          className={`inline-flex items-center gap-0.5 rounded-full bg-amber-100 font-semibold text-amber-800 ${size}`}
          title={`Personal best for this workout \u2014 fastest of ${comparison.comparableCount} comparable results`}
        >
          <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M10 1.5l2.47 5.01 5.53.8-4 3.9.94 5.5L10 14.1l-4.94 2.6.94-5.5-4-3.9 5.53-.8L10 1.5z" />
          </svg>
          Best
        </span>
      )}
      {pb && (
        <span
          className={`inline-flex items-center rounded-full ${
            pb.matched ? 'bg-amber-100 font-semibold text-amber-800' : 'bg-amber-50 text-amber-700'
          } ${size}`}
          title={pb.title}
        >
          {pb.label}
          {pb.date && <span className="ml-1 font-normal opacity-70">{pb.date}</span>}
        </span>
      )}
      {delta && (
        <span
          className={`inline-flex items-center rounded-full font-medium ${tone} ${size}`}
          title={delta.title}
        >
          {delta.label}
          <span className="ml-1 font-normal opacity-70">vs last</span>
        </span>
      )}
    </div>
  );
}
